import React, { createContext, useContext, useState, useEffect } from 'react';

const ModeContext = createContext();

export const useMode = () => {
  const context = useContext(ModeContext);
  if (!context) {
    throw new Error('useMode must be used within ModeProvider');
  }
  return context;
};

export const ModeProvider = ({ children }) => {
  const [mode, setMode] = useState('adults'); // 'kids' or 'adults' — default is adult
  const [language, setLanguage] = useState('tr'); // 'tr', 'en', 'ar'

  // Load from localStorage on mount
  useEffect(() => {
    const savedMode = localStorage.getItem('hafizim_mode');
    if (savedMode) setMode(savedMode);
    // Language is forced to Turkish — selector hidden.
    localStorage.setItem('hafizim_language', 'tr');
  }, []);

  // Save to localStorage on change
  useEffect(() => {
    localStorage.setItem('hafizim_mode', mode);
  }, [mode]);

  useEffect(() => {
    localStorage.setItem('hafizim_language', language);
  }, [language]);

  const toggleMode = () => {
    setMode(prev => prev === 'kids' ? 'adults' : 'kids');
  };

  const changeLanguage = (lang) => {
    setLanguage(lang);
  };

  const isKidsMode = mode === 'kids';
  const isAdultsMode = mode === 'adults';

  const getColors = () => {
    if (isKidsMode) {
      return {
        background: '#F0F9FF',
        primary: '#4ADE80',
        secondary: '#FACC15',
        accent: '#F472B6',
        text: '#1E293B',
        cardBg: '#FFFFFF',
        border: '#E2E8F0'
      };
    }
    return {
      background: '#F5F5F4',
      primary: '#064E3B',
      secondary: '#D97706',
      accent: '#14B8A6',
      text: '#1C1917',
      cardBg: '#FFFFFF',
      border: '#E7E5E4'
    };
  };

  const getButtonClass = () => {
    if (isKidsMode) {
      return 'h-14 text-lg font-bold rounded-full shadow-[0_4px_0_0_rgba(0,0,0,0.2)] active:shadow-none active:translate-y-1 transition-all';
    }
    return 'h-10 text-sm font-medium rounded-md border border-input bg-background hover:bg-accent hover:text-accent-foreground transition-colors';
  };

  const getCardClass = () => {
    if (isKidsMode) {
      return 'bg-white border-2 border-slate-100 rounded-3xl p-6 hover:scale-[1.02] transition-transform';
    }
    return 'bg-white/50 backdrop-blur-sm border border-stone-200 rounded-xl p-6';
  };

  return (
    <ModeContext.Provider
      value={{
        mode,
        setMode,
        toggleMode,
        isKidsMode,
        isAdultsMode,
        language,
        changeLanguage,
        getColors,
        getButtonClass,
        getCardClass
      }}
    >
      {children}
    </ModeContext.Provider>
  );
};
